import { useMemo, useState } from "react";
import { EXERCISE_NAMES, EXERCISE_NAMES_LIST } from "../exerciseNames";
import type { WorkoutPlanDto } from "../types";
import { DEFAULT_CYCLE_WEEKS, WEEKDAYS, cleanPlan, emptyDay, planIsSendable } from "../types";
import { saveAthletePlan } from "../db";
import { DayTable } from "./DayTable";

type SendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; planName: string }
  | { kind: "failed"; message: string };

interface Props {
  /** Whose list the plan is written into. */
  athleteUid: string;
  athleteName: string;
  /** Set when a coach is writing for someone else. */
  coachUid?: string;
  /** An existing plan to edit, or undefined to write a new one. */
  initialPlan?: WorkoutPlanDto;
  onDone: () => void;
}

function blankPlan(): WorkoutPlanDto {
  return {
    id: "",
    name: "",
    createdAtMillis: 0,
    nextSessionIndex: 0,
    days: [emptyDay("monday")],
    progressiveOverload: {
      percentage: 0,
      cycleWeeks: DEFAULT_CYCLE_WEEKS,
      currentWeek: 0,
      missedExercises: 0,
    },
  };
}

export function PlanEditor({ athleteUid, athleteName, coachUid, initialPlan, onDone }: Props) {
  const [plan, setPlan] = useState<WorkoutPlanDto>(initialPlan ?? blankPlan);
  const [state, setState] = useState<SendState>({ kind: "idle" });

  const usedWeekdays = useMemo(() => plan.days.map((day) => day.weekday), [plan.days]);
  const canSend = planIsSendable(plan) && state.kind !== "sending";

  function addDay() {
    // A new day lands on the first weekday not already taken, so two tables never collide.
    const free = WEEKDAYS.find((weekday) => !usedWeekdays.includes(weekday));
    if (!free) return;
    setPlan({ ...plan, days: [...plan.days, emptyDay(free)] });
  }

  async function send() {
    setState({ kind: "sending" });
    try {
      await saveAthletePlan(athleteUid, cleanPlan(plan), coachUid);
      setState({ kind: "sent", planName: plan.name });
      onDone();
    } catch (cause) {
      setState({
        kind: "failed",
        message: cause instanceof Error ? cause.message : "Could not save the plan.",
      });
    }
  }

  return (
    <>
      <section className="card">
        <div className="plan-head">
          <h2>{initialPlan ? "Edit plan" : "New plan"}</h2>
          <span className="tag">for {athleteName}</span>
          <button className="ghost" onClick={onDone}>
            Back to athletes
          </button>
        </div>
        <div className="plan-head">
          <label className="grow">
            Plan name
            <input
              value={plan.name}
              placeholder="e.g. Off-season block 1"
              onChange={(event) => setPlan({ ...plan, name: event.target.value })}
            />
          </label>
          <label>
            Overload %
            <input
              type="number"
              min={0}
              max={50}
              value={plan.progressiveOverload.percentage}
              onChange={(event) =>
                setPlan({
                  ...plan,
                  progressiveOverload: {
                    ...plan.progressiveOverload,
                    percentage: Number(event.target.value) || 0,
                  },
                })
              }
            />
          </label>
          <label>
            Cycle weeks
            <input
              type="number"
              min={1}
              max={12}
              value={plan.progressiveOverload.cycleWeeks}
              onChange={(event) =>
                setPlan({
                  ...plan,
                  progressiveOverload: {
                    ...plan.progressiveOverload,
                    cycleWeeks: Number(event.target.value) || 1,
                  },
                })
              }
            />
          </label>
        </div>
        <p className="hint">
          Leave the overload percentage at 0 to switch progressive overload off for this plan.
        </p>
      </section>

      {plan.days.map((day, index) => (
        <DayTable
          key={index}
          day={day}
          usedWeekdays={usedWeekdays}
          onChange={(next) =>
            setPlan({ ...plan, days: plan.days.map((d, i) => (i === index ? next : d)) })
          }
          onRemove={() =>
            setPlan({ ...plan, days: plan.days.filter((_, i) => i !== index) })
          }
        />
      ))}

      <button className="ghost" onClick={addDay} disabled={plan.days.length >= WEEKDAYS.length}>
        + Add day
      </button>

      <section className="card send">
        <button onClick={send} disabled={!canSend}>
          {state.kind === "sending" ? "Saving…" : `Save to ${athleteName}`}
        </button>
        {state.kind === "failed" && <p className="error">{state.message}</p>}
      </section>

      {/* The browser's own completion, fed the apps' library, for every exercise field on the
          page: a coach typing "Panca" is offered the spelling the athlete's phone uses, so the
          lift keeps one history. Once here rather than once per day, so its id stays unique. */}
      <datalist id={EXERCISE_NAMES_LIST}>
        {EXERCISE_NAMES.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
    </>
  );
}
